import { z } from 'zod';
/**
 * Tableau Server 인증 방식.
 * - PAT: Personal Access Token (token name + token value). 권장.
 * - Basic: username + password.
 *
 * 두 방식 모두 시크릿 (tokenValue / password) 은 dataSources.passwordEncrypted 에 저장.
 * tokenName / username 은 dataSources.username 컬럼 재사용.
 * authKind 와 siteContentUrl 은 dataSources.extraConfig 에 보관.
 */
export const TableauAuthKindSchema = z.enum(['pat', 'basic']);
/**
 * Tableau extraConfig 표현. dataSources.extraConfig 에 그대로 저장된다.
 * siteContentUrl 이 빈 문자열이면 default site 를 의미.
 */
export const TableauExtraConfigSchema = z.object({
    authKind: TableauAuthKindSchema,
    siteContentUrl: z.string().max(255).default(''),
});
/** 자원 종류 — datasource (게시된 데이터원본) 또는 workbook (통합문서). */
export const TableauResourceTypeSchema = z.enum(['datasource', 'workbook']);
/**
 * 자원 추출 방식.
 * - extract: REST API hasExtracts=true. 추출본(.hyper) 보유 → 통합 가능.
 * - flow_output: Tableau Prep 흐름의 출력. REST 상으론 live(hasExtracts=false) 로 보이지만
 *   실제로는 .hyper 로 게시됨. Metadata API(upstreamFlows)로 확인 → 통합 가능.
 * - live: 실제 라이브 연결(추출본 없음) → 통합 불가.
 */
export const TableauExtractTypeSchema = z.enum(['extract', 'live', 'flow_output']);
/** 통합 진행 단계 (UI/audit 용). */
export const TableauIntegrationStageSchema = z.enum([
    'queued',
    'signin',
    'download',
    'extract_hyper',
    'convert_duckdb',
    'upload',
    'register_duckdb',
    'cleanup',
    'done',
    'failed',
]);
export const TableauResourceSchema = z.object({
    id: z.string().uuid(),
    dataSourceId: z.string().uuid(),
    resourceType: TableauResourceTypeSchema,
    /** Tableau 내부 LUID (signin 응답으로 받은 식별자). */
    tableauLuid: z.string(),
    /** 프로젝트 계층 — '/root/sub1/sub2' 형태. 루트 자원은 ''. */
    projectPath: z.string(),
    name: z.string(),
    /** 'extract'/'flow_output' 은 통합 가능, 'live' 는 (Metadata API 확인 시) 통합 거부. */
    extractType: TableauExtractTypeSchema,
    /** flow_output 인 경우 출력을 만든 Prep 흐름 이름. 그 외 null. UI 라벨용. */
    upstreamFlowName: z.string().nullable(),
    description: z.string().nullable(),
    /** null 이면 수동만. 5필드 cron 식 (분 시 일 월 요일). */
    scheduleCron: z.string().nullable(),
    lastIntegratedAt: z.string().nullable(),
    lastDurationMs: z.number().int().nullable(),
    /** EWMA 평균. 첫 통합에서는 lastDurationMs 와 동일. */
    avgDurationMs: z.number().int().nullable(),
    /** 통합으로 만든 duckdb 데이터소스 id. 미통합/통합실패 후 cleanup 되면 null. */
    duckdbDataSourceId: z.string().uuid().nullable(),
    syncedAt: z.string(),
    /** 자원 sync 시 발견 안 되면 timestamp 마킹 — 트리에서 숨김. */
    removedAt: z.string().nullable(),
    /** 진행 중 통합 시작 시각. NULL 이면 idle. UI 가 spinner / 경과시간 표시 트리거. */
    integrateStartedAt: z.string().nullable(),
    /** 진행 중 통합의 한글 단계 메시지 (예: "DuckDB 변환 중 — Extract.Extract (12345행)"). */
    integrateProgressItem: z.string().nullable(),
    /** 마지막 통합 실패 메시지. 정상 완료 시 NULL. */
    lastIntegrateError: z.string().nullable(),
});
export const TableauResourceListResponseSchema = z.object({
    resources: z.array(TableauResourceSchema),
    /** 데이터소스의 자원 카탈로그 마지막 동기화 시각. null = 아직 한 번도 동기화 안 됨. */
    structureSyncedAt: z.string().nullable(),
    /** 마지막 sync 워커가 남긴 에러 메시지. null = 정상 또는 미실행. */
    lastSyncError: z.string().nullable(),
    /** 진행 중인 sync 워커가 있으면 true — 화면에 진행 상태 표시 + 폴링 fast 로 전환. */
    syncRunning: z.boolean(),
    /** 진행 중 워커의 마지막 progress 메시지 (예: "listDatasources page 3"). syncRunning=false 면 null. */
    syncProgressItem: z.string().nullable(),
    /** 진행 중 워커의 시작 시각. 사용자에게 "X분째 진행 중" 표시용. */
    syncStartedAt: z.string().nullable(),
    /**
     * 마지막 sync 시점에 Tableau Metadata API(GraphQL) 사용 가능 여부.
     * true 면 흐름 출력 자동 분류(flow_output)가 동작 → live 는 진짜 라이브로 신뢰.
     * false 면 분류 불가 → UI 가 live 자원에 "Live 통합 시도" 폴백 버튼을 노출.
     */
    metadataApiAvailable: z.boolean(),
});
/**
 * "통합" 버튼 요청.
 * - attemptLive: live 로 분류된 자원을 강제로 통합 시도. Metadata API 미가용 배포에서
 *   UI 의 "Live 통합 시도" 버튼이 사용. 다운로드한 .tdsx 에 .hyper 가 있으면 성공,
 *   없으면(진짜 라이브) 워커가 명확한 에러로 거부.
 */
export const IntegrateTableauResourceRequestSchema = z
    .object({ attemptLive: z.boolean().optional() })
    .optional();
export const IntegrateTableauResourceResponseSchema = z.object({
    jobKey: z.string(),
});
/**
 * 스케줄 설정. cron 이 null 이면 수동으로 되돌림.
 * 5필드 cron 식 (분 시 일 월 요일). UI 빠른선택 (매일 HH:MM 등) 이 cron 으로 변환해서 보낸다.
 */
export const SetTableauScheduleRequestSchema = z.object({
    cron: z
        .string()
        .nullable()
        .refine((v) => v === null || /^(\S+\s+){4}\S+$/.test(v.trim()), {
        message: '5필드 cron 식 (분 시 일 월 요일) 이 아닙니다.',
    }),
});
/**
 * 자원 description 편집. 자원 자체 메타이며, 통합 후 만들어진 duckdb 데이터소스의
 * description 으로도 함께 복사된다 (chat system 메시지에서 활용).
 */
export const UpdateTableauResourceDescriptionRequestSchema = z.object({
    description: z.string().max(2000).nullable(),
});
/**
 * Tableau 데이터소스 신규 등록 요청.
 * 일반 SQL 데이터소스와 필드 의미가 다르므로 별도 스키마로 분리.
 *
 * - serverUrl: https:// 로 시작하는 Tableau Server URL.
 * - siteContentUrl: 사이트 식별자 (URL 의 /#/site/<contentUrl>/ 부분). 빈 문자열이면 default site.
 * - auth: PAT 또는 Basic.
 */
export const CreateTableauDataSourceRequestSchema = z.object({
    name: z.string().min(1).max(80),
    serverUrl: z.string().url().max(255),
    siteContentUrl: z.string().max(255).default(''),
    auth: z.discriminatedUnion('kind', [
        z.object({
            kind: z.literal('pat'),
            tokenName: z.string().min(1).max(128),
            tokenValue: z.string().min(1).max(512),
        }),
        z.object({
            kind: z.literal('basic'),
            username: z.string().min(1).max(128),
            password: z.string().min(1).max(512),
        }),
    ]),
    description: z.string().max(2000).nullable().default(null),
});
/**
 * Tableau 연결 테스트 — 등록 전 검증. 신규 등록 요청과 같은 body 사용.
 */
export const TestTableauConnectionRequestSchema = CreateTableauDataSourceRequestSchema.omit({
    name: true,
    description: true,
});
/**
 * Tableau 데이터소스 수정 — 모든 필드 optional.
 * auth 가 지정되면 시크릿 교체 (kind 도 변경 가능).
 */
export const UpdateTableauDataSourceRequestSchema = z.object({
    name: z.string().min(1).max(80).optional(),
    serverUrl: z.string().url().max(255).optional(),
    siteContentUrl: z.string().max(255).optional(),
    auth: CreateTableauDataSourceRequestSchema.shape.auth.optional(),
    description: z.string().max(2000).nullable().optional(),
});
/**
 * duckdb 데이터소스 (Tableau 통합 산출물) 의 부모 자원 + 자동 생성된 Skill 조회 응답.
 * UI 가 duckdb DS 상세 페이지에서 통합/스케줄/Skill 버튼을 그릴 때 한 번에 필요한 정보를 모두 반환.
 *
 * 셋 다 null 이면 직접 만든 duckdb (사실상 없음) — UI 박스 자체를 안 그림.
 */
export const DuckdbTableauSourceResponseSchema = z.object({
    /** tableau_resources row. duckdb 파일을 만들어준 부모 자원. */
    resource: TableauResourceSchema.nullable(),
    /** 부모 tableau 데이터소스 id (mutation 호출 시 path param 으로 필요). */
    sourceTableauDataSourceId: z.string().uuid().nullable(),
    /** sync 완료 시 자동으로 만들어진 Skill (slug='tableau-<duckdbDsId8>'). */
    autoSkill: z
        .object({
        id: z.string().uuid(),
        slug: z.string(),
        name: z.string(),
    })
        .nullable(),
});
//# sourceMappingURL=data-source-tableau.js.map